import { formatLocale } from "d3-format";
import { timeFormat } from "d3-time-format";
import { DateTime } from 'luxon';
import { toDateTime } from './date';

export const locale = formatLocale({
  decimal: ",",
  thousands: " ",
  grouping: [3],
  percent: "%",
  currency: "€"
});

export const pctConverter = (decimals = 0, suffix = null) => {
  let formatter = locale.format(`.${decimals}~%`);
  return v => `${formatter(v)}${suffix ? ` ${suffix}` : ""}`;
};

export const intConverter = (suffix = null) => {
  let formatter = locale.format(",.0f");
  return v => `${formatter(v)}${suffix ? ` ${suffix}` : ""}`;
};

export const padZip = zip => {
  zip = `${zip}`;
  return zip.length === 4 ? `0${zip}` : zip;
};

export const padIris = iris => {
  iris = `${iris}`;
  return iris.length === 8 ? `0${iris}` : iris;
};

const isIrisFormat = input => /(~iris)$/.test(input.formatters.value.format);
const isZipFormat = input => /(~zip)$/.test(input.formatters.value.format);
const hasSpecFormat = input => input.formatters.value && input.formatters.value.format;
const isNumeric = input => /(~f|~%)$/.test(input.formatters.value.format);
const isTime = input => /%y/i.test(input.formatters.value.format);

export const buildConverter = (from, withSuffixDefault = true, withValueMap = true) => {

  let formatter;

  if (hasSpecFormat(from)) {

    if (isNumeric(from)) {
      formatter = locale.format(`,${from.formatters.value.format}`);
    } else if (isTime(from)) {
      formatter = v => {
        if (v) {
          const d = new Date(v);
          if (!isNaN(d.valueOf())) {
            return timeFormat(from.formatters.value.format)(d);
          }
          return "";
        }
        return null;
      }

    } else if(isZipFormat(from)){
      formatter = padZip;
    } else if(isIrisFormat(from)){
      formatter = padIris;
    } else {
      formatter = locale.format(from.formatters.value.format);
    }
  } else {
    formatter = v => v != null ? `${v}` : "";
  }

  const valueTransform = (withValueMap && from?.formula?.metadata?.valueMap) ?
    v => from.formula.metadata.valueMap[v] || formatter(v)
    : v => formatter(v);

  return (v, { withSuffix = withSuffixDefault } = {}) => {

    if (v != null) {

      if (withSuffix && from.formatters.value && (from.formatters.value.suffix || "") != "") {
        return `${valueTransform(v)} ${from.formatters.value.suffix}`;
      }
      return valueTransform(v);
    } else {
      return "";
    }
  }
};

export const numericConverter = buildConverter({ formatters: { value: { format: ".1~f" } } });

export const getConverterPrecision = (from, def = 0) => {
  if (hasSpecFormat(from)) {
    let { format } = from.formatters.value;
    if (/\d/.test(format)) {
      return +(format.replace(/[^\d]/g, "")) + (/~%/.test(format) ? 2 : 0);
    } else {
      return 0;
    }
  }
  return def;
};


export const changeFormatPrecision = (format, precision) => {
  return format && format.replace(/^.\d+~(.)/, `.${precision}~\$1`);
};

export const buildReverser = (from) => {
  if (hasSpecFormat(from)) {
    return s => {
      let tranforms = {
        "%": Math.pow(10, -2)
      };
      const suffix = from.formatters.value.format.match(/[^\d]$/g)?.[0];
      s = s.trim().replace(/\s+/g, "").replace(/,/g, ".");
      if (suffix) {
        s = s.replace(new RegExp(suffix+"$", "g"), "")
      }
      s = s.replace(/[^\d]+$/g, "");
      return parseFloat(s) * (suffix ? (tranforms[suffix] ?? 1) : 1);
    }
  } else {
    return s => parseFloat(s.trim().replace(/\s+/g, "").replace(/,/g, "."));
  }
}

export const getUnitForLayer = lyr => {
  let f = lyr.formatters.value.format;
  switch (true) {
    case (/%$/g).test(f): return "%";
    case (lyr.formatters.value.suffix || "") != "": return lyr.formatters.value.suffix;
    default: return lyr.name;
  }
};

export const nutsInfoLabel = (nutsInfo) => {
  if (nutsInfo) {
    const {
      fromGeomType,
      nuts5_name = null,
      nuts4_name = null,
      nuts3_name = null
    } = nutsInfo;
    return nutsInfo[`${fromGeomType}_name`] || nuts5_name || nuts4_name || nuts3_name || "";
  }
  return "";
};

export const nutsInfoCode = (nutsInfo) => {
  if (nutsInfo) {
    return nutsInfo[`${nutsInfo.fromGeomType}_code`];
  }
  return "";
};

/**
 * Date Format helper
 */

export const formatDate = (d: Date | DateTime | string | number, format = "dd/MM/yyyy - HH:mm") => toDateTime(d).toFormat(format, { locale: "fr" });

export const fileSize = (bytes, si = true, emptyString = '0') => {

  if( bytes === 0 ) return emptyString;

  var thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) return bytes + ' B';

  var units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  var u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);

  return bytes.toFixed(1) + ' ' + units[u];
};

export const normalize = str => str.normalize('NFD')
  .trim()
  .toLocaleLowerCase()
  .replace(/-/g, '_')
  .replace(/[\u0300-\u036f]/g, "");

export const camelize = str => {
  str = str.replace(/[\W_]+/g, " ");

  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (/\s+/.test(match)) {
      return "";
    }
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const firstUpperCase = (str) => `${str.charAt(0).toLocaleUpperCase()}${str.slice(1)}`;

export const analysisMetasToComputedArray = (analysisMetas, { editable }) => {
  if (!analysisMetas) analysisMetas = {};
  const computed = [];

  if(analysisMetas.template && analysisMetas.template.name){
    computed.push({label: 'Template', value: analysisMetas.template.name});
  }

  if (typeof analysisMetas.scoreRatio !== 'undefined') {
    const tmp = [];
    const potential = +(((1 - +analysisMetas.scoreRatio) * 100).toFixed(0));
    const attractivity = +((+analysisMetas.scoreRatio * 100).toFixed(0));

    if (potential) {
      tmp.push(`Potentiel optimal (${potential} %)`);
    }
    if (attractivity) {
      tmp.push(`Attractivité (${attractivity} %)`);
    }

    computed.push({ label: 'Implantation', value: tmp.join(' et ') });
  }
  if (analysisMetas.vehicle && (analysisMetas.duration || analysisMetas.distance)) {
    computed.push({
      label: 'Zone de chalandise',
      value: `${analysisMetas.duration || analysisMetas.distance}
              ${analysisMetas.duration ? 'min' : 'km'}${' '}
              ${analysisMetas.vehicle === 'car' ? 'en voiture' : 'à pied'}`
    });
  }

  // All other
  computed.push({ name: 'description', label: 'Description', value: analysisMetas.description || '', editable, default: '' });
  return computed;
};

export const getSpreadedFileNameWithExt = (url, maxLength = 20) => {
  const ext = url.split('.').pop();

  if ((url.length - ext.length) > maxLength) {
    return `${url.split('/').pop().substring(0, url.length - ext.length).substring(0, maxLength)}....${ext}`;
  }

  return url;
};
